import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Stack, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Autocomplete,
    FormControlLabel, Checkbox, Tooltip, IconButton, CircularProgress, Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import CancelIcon from "@mui/icons-material/HighlightOffOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import api, { apiError, tokens as session } from "../api";
import AdminHeader, { adminPage } from "../components/AdminHeader";
import AppFooter from "../components/AppFooter";

/*
 * SCADA licence keys.
 *
 * What we sell alongside the AI: a serial key carrying how many devices or tags an install
 * may have, how many users, for how long, which drivers and which modules. The customer
 * types it into their FDM, which binds it to that machine's disk and asks the licence
 * server to issue the licence.
 *
 * The form is the old licence-tool's, field for field. Those names are read by FDM builds
 * already running on customer hardware - a tidier name here would be a licence that
 * activates and then quietly switches a module off.
 */

const DRIVERS = [
    "Power Modbus", "Modbus TCP", "Modbus RTU", "Generic MQTT", "WT-400", "WT-400-MQTT",
    "Robustel MQTT", "SE PME DB", "OPCUA", "BACNET-IP", "IEC-61850", "AB MicroLogix",
    "S7-200 Smart", "DNP3", "IEC-104", "S7-1200",
];

const MODULES = [
    ["isEmail", "Email"],
    ["isNotification", "Notification"],
    ["isTemplate", "Template"],
    ["isMqttServer", "MQTT Server"],
    ["isOPCUAServer", "OPCUA Server"],
    ["isDownTime", "Down Time Report"],
    ["isJobManagement", "Job Management"],
    ["isAPI", "API Integration"],
    ["isNodered", "Nodered"],
];

const BLANK = {
    /*
     * With a payment link by default, because that is how a licence is normally sold.
     * Turning it off is the old tool's way: paid for somewhere else, so the key works at
     * once and runs for however many days are typed in.
     */
    withPaymentLink: true,
    billingPeriod: "monthly",
    amount: "",
    activationDays: "",
    unlimited: false,
    activationType: "Device Based",
    activationCount: "",
    userCount: "",
    driver: [],
    ...Object.fromEntries(MODULES.map(([k]) => [k, false])),
};

const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    /*
     * Disabled, but still meant to be read.
     *
     * A field is disabled here because its value cannot change, not because it does not
     * matter - the activation type is one of the first things somebody checks. MUI greys
     * it out with -webkit-text-fill-color, which on this dark paper leaves it all but
     * invisible, so the colour is set back to something legible and only the border is
     * allowed to fade.
     */
    "& .MuiOutlinedInput-root.Mui-disabled": {
        bgcolor: "rgba(255,255,255,.02)",
        "& fieldset": { borderColor: "rgba(255,255,255,.08)" },
        "& .MuiOutlinedInput-input": { WebkitTextFillColor: "rgba(255,255,255,.65)" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.5)" },
    "& .MuiInputLabel-root.Mui-disabled": { color: "rgba(255,255,255,.38)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
    "& .MuiSelect-icon, & .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
        color: "rgba(255,255,255,.55)",
    },
    "& .MuiChip-root": { bgcolor: "rgba(255,255,255,.1)", color: "#fff" },
};

const dialogPaper = {
    sx: {
        bgcolor: "#0C1A12", color: "#fff", borderRadius: 3,
        border: "1px solid rgba(255,255,255,.12)", backgroundImage: "none",
    },
};

const th = {
    color: "rgba(255,255,255,.72)", fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: .6, whiteSpace: "nowrap",
    bgcolor: "rgba(255,255,255,.06)",
    borderBottom: "1px solid rgba(255,255,255,.18)",
};
const td = { color: "#fff", borderColor: "rgba(255,255,255,.07)" };

/* Pending is not an error and a failed renewal is not a cancellation; the colours say so. */
const chipSx = (bg, fg) => ({
    bgcolor: bg, color: fg, fontWeight: 600,
    border: "1px solid rgba(255,255,255,.14)",
});
const STATUS = {
    pending: { label: "Awaiting payment", sx: chipSx("rgba(255,255,255,.07)", "rgba(255,255,255,.6)") },
    active: { label: "Active", sx: chipSx("rgba(46,125,32,.25)", "#9FE08C") },
    retrying: { label: "Retrying payment", sx: chipSx("rgba(214,158,46,.2)", "#F3C969") },
    halted: { label: "Payment failed", sx: chipSx("rgba(214,158,46,.2)", "#F3C969") },
    cancelled: { label: "Cancelled", sx: chipSx("rgba(229,62,62,.18)", "#FF9B9B") },
    /* Working, but nothing is being charged for it - blue rather than green, so a row
       that will never bring money in is never mistaken for one that does. */
    unbilled: { label: "Without payment link", sx: chipSx("rgba(49,130,206,.22)", "#9CC9F0") },
};

/*
 * Pending means two different things, and the row has to say which.
 *
 * A key nobody has bought yet is waiting for its first payment. A key that has been paid
 * before and is pending again is a renewal Razorpay could not collect and will retry -
 * a customer to leave alone for a day, not one to chase for payment. Only money arriving
 * ever sets `lastPaymentAt`, so it is the thing that separates them.
 */
const statusOf = (row) =>
    row.status === "pending" && row.lastPaymentAt ? "retrying" : row.status;

/*
 * How long this licence runs, as the plant will actually see it.
 *
 * The number stamped on the key is only a guess made before anybody paid. Once Razorpay
 * has a cycle running, the plant's window is counted to `paidTill` instead - a calendar
 * month being 28 to 31 days - and support reading this column should see the same figure
 * the plant gets, not the guess. Matches utils/licenceWindow.js.
 */
/*
 * Round action buttons, as on the tokens table - the same two acts should look the same
 * in both places. A disabled one keeps its circle and loses its colour, so the row does
 * not change shape when a subscription is cancelled.
 */
const actionAvatar = (bg, off = false) => ({
    width: 28, height: 28,
    bgcolor: off ? "rgba(255,255,255,.06)" : bg,
    color: off ? "rgba(255,255,255,.25)" : "#fff",
});

const DAY = 24 * 60 * 60 * 1000;
const daysLeft = (from, days) =>
    Math.ceil((new Date(from).getTime() + days * DAY - Date.now()) / DAY);

const validity = (row) => {
    /*
     * An unbilled key runs a fixed number of days from the day its plant activated, so
     * until that happens there is nothing to count down - the number is all there is.
     */
    if (row.status === "unbilled") {
        if (row.activationTime === "Unlimted") return "Unlimited";
        if (!row.activatedAt) return `${row.activationDays} days`;
        const left = daysLeft(row.activatedAt, row.activationDays);
        return left > 0 ? `${left} days left` : "Expired";
    }

    if (!row.paidTill) return `${row.activationDays} days`;

    const left = Math.ceil((new Date(row.paidTill).getTime() - Date.now()) / DAY);
    return left > 0 ? `${left} days left` : "Expired";
};

export default function AdminLicences() {
    const navigate = useNavigate();
    const [rows, setRows] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(BLANK);
    const [busy, setBusy] = useState(false);
    const [made, setMade] = useState(null);     // the key just cut, shown once large
    const [doomed, setDoomed] = useState(null);   // being deleted, pending confirmation
    const [stopping, setStopping] = useState(null); // having its subscription cancelled
    const [editing, setEditing] = useState(null);   // unbilled key having its days changed

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/console/product-keys");
            setRows(Array.isArray(data?.data) ? data.data : []);
        } catch (e) {
            if (e?.response?.status === 401) { session.console.clear(); navigate("/admin"); }
            else { toast.error(apiError(e)); setRows([]); }
        }
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

    /*
     * The licence part is the same either way; what differs is the one number that holds
     * it up - a price for a subscription, a run of days for a key nobody will be billed
     * for. Unlimited supplies that number itself.
     */
    const canCreate = Boolean(form.activationType && form.activationCount && form.userCount
        && (form.withPaymentLink
            ? Number(form.amount) > 0
            : form.unlimited || Number(form.activationDays) > 0));

    const create = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/console/product-keys", form);
            setOpen(false);
            setForm(BLANK);
            setMade(data.data);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    /*
     * Renewing a licence nobody is billed for: raise the days.
     *
     * Opened with what the key already has, because the common change is "another year
     * on top", and somebody typing into an empty box is one slip away from shortening a
     * running site instead of extending it.
     */
    const openEdit = (row) => setEditing({
        row,
        activationDays: String(row.activationDays || ""),
        unlimited: row.activationTime === "Unlimted",
        activationType: row.activationType || "Device Based",
        activationCount: String(row.activationCount || ""),
        userCount: String(row.userCount || ""),
        driver: Array.isArray(row.driver) ? row.driver : [],
        ...Object.fromEntries(MODULES.map(([k]) => [k, !!row[k]])),
    });

    const setEdit = (k) => (e) => setEditing((s) => ({ ...s, [k]: e.target.value }));
    const toggleEdit = (k) => (e) => setEditing((s) => ({ ...s, [k]: e.target.checked }));

    const saveEdit = async () => {
        setBusy(true);
        try {
            const { row, unlimited, ...rest } = editing;
            await api.patch(`/console/product-keys/${row._id}`, {
                ...rest,
                unlimited,
                activationDays: Number(editing.activationDays) || 0,
                activationCount: Number(editing.activationCount) || 0,
                userCount: Number(editing.userCount) || 0,
            });
            toast.success("Validity updated");
            setEditing(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const remove = async () => {
        setBusy(true);
        try {
            await api.delete(`/console/product-keys/${doomed._id}`);
            toast.success("Licence key deleted");
            setDoomed(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const cancelSub = async () => {
        setBusy(true);
        try {
            await api.post(`/console/product-keys/${stopping._id}/cancel`);
            toast.success("Subscription cancelled");
            setStopping(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied");
        } catch { toast.error("Could not copy - select the text and copy it by hand"); }
    };

    return (
        <Box sx={{ ...adminPage, display: "flex", flexDirection: "column" }}>
            <AdminHeader />

            <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
                <Stack direction="row" spacing={2}
                    sx={{ alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 24, letterSpacing: -0.5 }}>
                        Licence Keys
                    </Typography>
                    {/* Outlined, like Settings on the accounts page: the two admin screens
                        should not wear different buttons for the same kind of act. */}
                    <Button startIcon={<AddIcon />} onClick={() => setOpen(true)}
                        sx={{
                            borderRadius: 999, px: 2.5, color: "#9FE08C",
                            border: "1px solid rgba(123,217,95,.35)",
                            "&:hover": { bgcolor: "rgba(46,125,32,.18)" },
                        }}>
                        Generate Licence
                    </Button>
                </Stack>

                <Box sx={{
                    borderRadius: 1.5, overflow: "hidden", overflowX: "auto",
                    bgcolor: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)",
                }}>
                    {rows === null ? (
                        <Box sx={{ p: 5, textAlign: "center" }}><CircularProgress size={26} /></Box>
                    ) : (
                        <Table size="small" sx={{ tableLayout: "fixed", minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={th} align="center">Actions</TableCell>
                                    <TableCell sx={th} align="center">Serial key</TableCell>
                                    <TableCell sx={th} align="center">Email</TableCell>
                                    <TableCell sx={th} align="center">Type</TableCell>
                                    <TableCell sx={th} align="center">Count</TableCell>
                                    <TableCell sx={th} align="center">Users</TableCell>
                                    <TableCell sx={th} align="center">Validity</TableCell>
                                    <TableCell sx={th} align="center">Billing</TableCell>
                                    <TableCell sx={th} align="center">Status</TableCell>
                                    <TableCell sx={th} align="center">Link</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.serialKey} hover>
                                        {/* Two different acts, and the difference matters:
                                            cancelling stops the billing and leaves the record,
                                            deleting removes the key as though it was never cut.
                                            A customer who is leaving wants the first. */}
                                        <TableCell sx={td} align="center">
                                            <Stack direction="row" spacing={.5} sx={{ justifyContent: "center" }}>
                                                {/* Only where it means anything. On a
                                                    subscription the validity follows the
                                                    payments, so there is nothing here a
                                                    person should be setting by hand. */}
                                                {r.status === "unbilled" && (
                                                    <Tooltip title="Change validity">
                                                        <IconButton sx={{ p: .3 }} onClick={() => openEdit(r)}>
                                                            <Avatar sx={actionAvatar("#2E7D20")}>
                                                                <EditIcon sx={{ fontSize: 16 }} />
                                                            </Avatar>
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title={r.status === "cancelled"
                                                    ? "Subscription already cancelled"
                                                    : "Stop billing, keep the record"}>
                                                    <span>
                                                        <IconButton sx={{ p: .3 }}
                                                            disabled={r.status === "cancelled" || !r.razorpay?.subscriptionId}
                                                            onClick={() => setStopping(r)}>
                                                            <Avatar sx={actionAvatar("#8C6A1F", r.status === "cancelled" || !r.razorpay?.subscriptionId)}>
                                                                <CancelIcon sx={{ fontSize: 16 }} />
                                                            </Avatar>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Delete this key">
                                                    <IconButton sx={{ p: .3 }} onClick={() => setDoomed(r)}>
                                                        <Avatar sx={actionAvatar("#8C2F2F")}>
                                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={td} align="center">
                                            <Stack direction="row" spacing={.5}
                                                sx={{ alignItems: "center", justifyContent: "center" }}>
                                                <Typography sx={{ fontFamily: "monospace", fontSize: 13.5 }}>
                                                    {r.serialKey}
                                                </Typography>
                                                <Tooltip title="Copy key">
                                                    <IconButton size="small" onClick={() => copy(r.serialKey)}
                                                        sx={{ color: "rgba(255,255,255,.55)" }}>
                                                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                        {/* The FDM admin running on this key, learned when the
                                            plant activated. Blank until then - a key that has
                                            been paid for but never installed is worth seeing. */}
                                        <TableCell sx={td} align="center">
                                            {r.boundEmail ? (
                                                <Tooltip title={r.activatedAt
                                                    ? new Date(r.activatedAt).toLocaleString("en-IN", { hour12: false })
                                                    : ""}>
                                                    <Typography sx={{ fontSize: 13.5 }}>{r.boundEmail}</Typography>
                                                </Tooltip>
                                            ) : (
                                                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,.4)" }}>
                                                    not installed
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ ...td, color: "rgba(255,255,255,.75)" }} align="center">
                                            {r.activationType || "—"}
                                        </TableCell>
                                        <TableCell sx={td} align="center">{r.activationCount}</TableCell>
                                        <TableCell sx={td} align="center">{r.userCount}</TableCell>
                                        <TableCell sx={td} align="center">
                                            <Tooltip title={r.paidTill
                                                ? `Paid until ${new Date(r.paidTill).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                                                : ""}>
                                                <Chip size="small" label={validity(r)}
                                                    sx={{
                                                        bgcolor: "rgba(46,125,32,.25)", color: "#9FE08C",
                                                        border: "1px solid rgba(255,255,255,.14)", fontWeight: 600,
                                                    }} />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ ...td, color: "rgba(255,255,255,.8)" }} align="center">
                                            {r.amount
                                                ? `Rs ${r.amount.toLocaleString("en-IN")} / ${r.billingPeriod === "yearly" ? "yr" : "mo"}`
                                                : "\u2014"}
                                        </TableCell>
                                        <TableCell sx={td} align="center">
                                            <Chip size="small" label={STATUS[statusOf(r)]?.label || r.status}
                                                sx={STATUS[statusOf(r)]?.sx || {}} />
                                        </TableCell>
                                        <TableCell sx={td} align="center">
                                            {r.razorpay?.shortUrl ? (
                                                <Tooltip title="Copy payment link">
                                                    <IconButton size="small" onClick={() => copy(r.razorpay.shortUrl)}
                                                        sx={{ color: "rgba(255,255,255,.6)" }}>
                                                        <LinkIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : "\u2014"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!rows.length && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center"
                                            sx={{ ...td, py: 5, color: "rgba(255,255,255,.5)" }}>
                                            No licence keys yet
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Box>

            {/* ---------- delete ---------- */}
            <Dialog open={Boolean(doomed)} onClose={() => setDoomed(null)} maxWidth="xs" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete this licence key?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 14.5 }}>
                        <b style={{ fontFamily: "monospace" }}>{doomed?.serialKey}</b> will be removed,
                        and its Razorpay subscription cancelled so nothing is charged again.
                    </Typography>
                    {doomed?.boundEmail && (
                        <Typography sx={{ color: "#F3C969", fontSize: 13.5, mt: 1.5 }}>
                            This key is installed on <b>{doomed.boundEmail}</b>. That plant keeps
                            working until its current licence runs out, and then stops - with
                            nothing left here to explain why. To stop the billing and keep the
                            record, cancel the subscription instead.
                        </Typography>
                    )}
                    <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 13, mt: 1.5 }}>
                        This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDoomed(null)} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                    <Button variant="contained" disabled={busy} onClick={remove}
                        sx={{ borderRadius: 999, px: 3, bgcolor: "#B3261E", "&:hover": { bgcolor: "#8F1D17" } }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ---------- cancel the subscription ---------- */}
            <Dialog open={Boolean(stopping)} onClose={() => setStopping(null)} maxWidth="xs" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Cancel this subscription?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 14.5 }}>
                        Nothing more will be charged for
                        <b style={{ fontFamily: "monospace" }}> {stopping?.serialKey}</b>.
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13.5, mt: 1.5 }}>
                        The licence already paid for runs to its last day and then expires. The key
                        stays in this list, so the history is still here.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setStopping(null)} sx={{ color: "rgba(255,255,255,.65)" }}>
                        Keep it
                    </Button>
                    <Button variant="contained" disabled={busy} onClick={cancelSub}
                        sx={{ borderRadius: 999, px: 3 }}>Cancel subscription</Button>
                </DialogActions>
            </Dialog>

            {/* ---------- change the validity of an unbilled key ---------- */}
            <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Edit licence</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
                            {/* Fixed when the key was cut. The count beside it means
                                devices or tags, and swapping that underneath a running
                                site would give it a licence nobody chose. */}
                            <TextField size="small" label="Activation type" sx={darkField} disabled
                                value={editing?.activationType || "Device Based"} />
                            <TextField size="small" type="number" sx={darkField}
                                label={editing?.activationType === "Tag Based" ? "Tags" : "Devices"}
                                value={editing?.activationCount || ""} onChange={setEdit("activationCount")} />
                        </Box>

                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
                            <TextField select size="small" label="Validity" sx={darkField}
                                value={editing?.unlimited ? "unlimited" : "limited"}
                                onChange={(e) => setEditing((s) => ({ ...s, unlimited: e.target.value === "unlimited" }))}>
                                <MenuItem value="limited">Limited</MenuItem>
                                <MenuItem value="unlimited">Unlimited</MenuItem>
                            </TextField>
                            {!editing?.unlimited && (
                                <TextField size="small" type="number" sx={darkField}
                                    label="Validity (days)" value={editing?.activationDays || ""}
                                    onChange={setEdit("activationDays")} />
                            )}
                        </Box>

                        <TextField size="small" type="number" label="Users" sx={darkField}
                            value={editing?.userCount || ""} onChange={setEdit("userCount")} />

                        <Autocomplete
                            multiple options={DRIVERS} value={editing?.driver || []} sx={darkField}
                            onChange={(e, v) => setEditing((s) => ({ ...s, driver: v }))}
                            slotProps={{
                                paper: {
                                    sx: {
                                        bgcolor: "#0C1A12", color: "#fff",
                                        border: "1px solid rgba(255,255,255,.14)",
                                    },
                                },
                            }}
                            renderInput={(p) => <TextField {...p} size="small" label="Drivers" />} />

                        <Box>
                            <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 12.5, mb: .5 }}>
                                Modules
                            </Typography>
                            {/* Three across, as on the create dialog. The same list read
                                in the same shape in both places, so a person comparing
                                what a key has against what it was given is not also
                                re-learning the layout. */}
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
                                {MODULES.map(([key, label]) => (
                                    <FormControlLabel key={key} label={label}
                                        sx={{ color: "rgba(255,255,255,.85)" }}
                                        control={
                                            <Checkbox size="small" checked={Boolean(editing?.[key])}
                                                onChange={toggleEdit(key)}
                                                sx={{
                                                    color: "rgba(255,255,255,.4)",
                                                    "&.Mui-checked": { color: "#7BD95F" },
                                                }} />
                                        } />
                                ))}
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setEditing(null)} sx={{ color: "rgba(255,255,255,.6)" }}>Cancel</Button>
                    <Button onClick={saveEdit} variant="contained"
                        disabled={busy
                            || !(Number(editing?.activationCount) > 0)
                            || !(Number(editing?.userCount) > 0)
                            || (!editing?.unlimited && !(Number(editing?.activationDays) > 0))}
                        sx={{ bgcolor: "#2E7D20", "&:hover": { bgcolor: "#276B1B" } }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ---------- generate ---------- */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Generate licence</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        {/* What the licence allows, then how it is paid for. Each row
                            decides what the next one asks, so the questions only ever
                            narrow - nothing is filled in and then made irrelevant. */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
                            <TextField select size="small" label="Activation type" sx={darkField}
                                value={form.activationType} onChange={set("activationType")}>
                                <MenuItem value="Device Based">Device Based</MenuItem>
                                <MenuItem value="Tag Based">Tag Based</MenuItem>
                            </TextField>
                            {/* The count means devices or tags depending on the choice above,
                                so the label follows it rather than saying "count". */}
                            <TextField size="small" type="number" sx={darkField}
                                label={form.activationType === "Tag Based" ? "Tags" : "Devices"}
                                value={form.activationCount} onChange={set("activationCount")} />
                        </Box>

                        {/* Cutting a key on the wrong one of these means either a customer
                            who is never billed or one who is billed twice. */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
                            <TextField select size="small" label="Payment type" sx={darkField}
                                value={form.withPaymentLink ? "link" : "nolink"}
                                onChange={(e) => setForm((f) => ({ ...f, withPaymentLink: e.target.value === "link" }))}>
                                <MenuItem value="link">With payment link</MenuItem>
                                <MenuItem value="nolink">Without payment link</MenuItem>
                            </TextField>

                            {form.withPaymentLink ? (
                                <TextField select size="small" label="Plan" sx={darkField}
                                    value={form.billingPeriod} onChange={set("billingPeriod")}>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                    <MenuItem value="yearly">Yearly</MenuItem>
                                </TextField>
                            ) : (
                                <TextField select size="small" label="Validity" sx={darkField}
                                    value={form.unlimited ? "unlimited" : "limited"}
                                    onChange={(e) => setForm((f) => ({ ...f, unlimited: e.target.value === "unlimited" }))}>
                                    <MenuItem value="limited">Limited</MenuItem>
                                    <MenuItem value="unlimited">Unlimited</MenuItem>
                                </TextField>
                            )}
                        </Box>

                        {/* The one number the choice above still needs: a price, or a run
                            of days. Unlimited needs neither and so asks for nothing. */}
                        {form.withPaymentLink ? (
                            <TextField size="small" type="number" sx={darkField}
                                label={form.billingPeriod === "yearly" ? "Amount / year (Rs)" : "Amount / month (Rs)"}
                                value={form.amount} onChange={set("amount")} />
                        ) : !form.unlimited ? (
                            <TextField size="small" type="number" sx={darkField}
                                label="Validity (days)" value={form.activationDays}
                                onChange={set("activationDays")} />
                        ) : null}

                        <TextField size="small" type="number" label="Users" sx={darkField}
                            value={form.userCount} onChange={set("userCount")} />

                        <Autocomplete
                            multiple options={DRIVERS} value={form.driver} sx={darkField}
                            onChange={(e, v) => setForm((f) => ({ ...f, driver: v }))}
                            slotProps={{
                                paper: {
                                    sx: {
                                        bgcolor: "#0C1A12", color: "#fff",
                                        border: "1px solid rgba(255,255,255,.14)",
                                    },
                                },
                            }}
                            renderInput={(p) => <TextField {...p} size="small" label="Drivers" />}
                        />

                        <Box>
                            <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 12.5, mb: .5 }}>
                                Modules
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
                                {MODULES.map(([key, label]) => (
                                    <FormControlLabel key={key} label={label}
                                        sx={{ color: "rgba(255,255,255,.85)" }}
                                        control={
                                            <Checkbox size="small" checked={form[key]} onChange={toggle(key)}
                                                sx={{
                                                    color: "rgba(255,255,255,.4)",
                                                    "&.Mui-checked": { color: "#7BD95F" },
                                                }} />
                                        } />
                                ))}
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setOpen(false)} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                    <Button variant="contained" disabled={!canCreate || busy} onClick={create}
                        sx={{ borderRadius: 999, px: 3 }}>Generate</Button>
                </DialogActions>
            </Dialog>

            {/* ---------- the key, large enough to read aloud ---------- */}
            <Dialog open={Boolean(made)} onClose={() => setMade(null)} maxWidth="sm" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: "center" }}>
                        <CheckCircleIcon sx={{ color: "#7BD95F" }} /> Licence key created
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    {/* Two quite different next steps, so the dialog says the right one
                        rather than a sentence that covers both. */}
                    <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 14.5, mb: 2 }}>
                        {made?.status === "unbilled"
                            ? `This key works straight away, for ${made?.activationTime === "Unlimted" ? "as long as it is installed" : `${made?.activationDays} days from the day it is installed`}. Nothing will ever be charged for it.`
                            : "Send the payment link first. The key activates once the first payment lands - nothing on this screen switches it on."}
                    </Typography>

                    {made?.razorpay?.shortUrl && (
                        <>
                            <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 12.5, mb: .6 }}>
                                Payment link
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{
                                alignItems: "center", p: 1.4, mb: 2.5, borderRadius: 2,
                                bgcolor: "rgba(0,0,0,.35)", border: "1px solid rgba(255,255,255,.14)",
                            }}>
                                <Typography sx={{ fontSize: 13.5, wordBreak: "break-all", flex: 1 }}>
                                    {made.razorpay.shortUrl}
                                </Typography>
                                <IconButton onClick={() => copy(made.razorpay.shortUrl)} sx={{ color: "#7BD95F" }}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </>
                    )}

                    <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 12.5, mb: .6 }}>
                        Licence key
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{
                        alignItems: "center", p: 1.8, borderRadius: 2,
                        bgcolor: "rgba(0,0,0,.35)", border: "1px solid rgba(255,255,255,.14)",
                    }}>
                        <Typography sx={{
                            fontFamily: "monospace", fontSize: 18, letterSpacing: 1.5,
                            wordBreak: "break-all", flex: 1,
                        }}>
                            {made?.serialKey}
                        </Typography>
                        <IconButton onClick={() => copy(made.serialKey)} sx={{ color: "#7BD95F" }}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button variant="contained" onClick={() => setMade(null)}
                        sx={{ borderRadius: 999, px: 3 }}>Done</Button>
                </DialogActions>
            </Dialog>

            <AppFooter />
        </Box>
    );
}
